import { AppContext } from "vue";
import { useAuthStore, Variables } from "./global";
import { customAlert, customConfirm, customConfirmDelete } from './components/confirm-alert';
import { DeleteProps } from "./components/confirm-alert/utils";

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import arraySupport from 'dayjs/plugin/arraySupport';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(arraySupport);
dayjs.extend(advancedFormat);
window.dayjs = dayjs;

declare global {
    interface Window {
        customAlert: (appContext: AppContext, status: "success" | "danger" | "info" | "warning", message: string) => void;
        customConfirm: (appContext: AppContext, message: string) => Promise<boolean>;
        customConfirmDelete: (appContext: AppContext, data: DeleteProps) => Promise<boolean>;
        playAudio: () => void;
        pauseAudio: () => void;
        dayjs: (date?: dayjs.ConfigType) => dayjs.Dayjs;
        getTimezone: () => any;
    }
}

window.customAlert = customAlert;
window.customConfirm = customConfirm;
window.customConfirmDelete = customConfirmDelete;
window.getTimezone = () => {
    var value = 'Africa/Dar_es_Salaam';

    try {
        if (typeof Intl != 'undefined') {
            value = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
    } catch (error) {
        console.error('Timezone not supported');
    }

    return value;
}

// import.meta.env.HOST_URL = window.location.origin;
window.$baseUrl = window.location.origin;
window.$prefixUrl = import.meta.env.VITE_APP_BASE_URL;
window.$moduleName = import.meta.env.VITE_MODULE_NAME;
window.$isDesktop = false;
window.$isDev = import.meta.env.DEV;

window.$consoleLog = (...args: any[]) => {
    if (window.$isDev) {
        console.log(...args);
    }
}

window.$consoleDebug = (...args: any[]) => {
    if (window.$isDev) {
        console.debug(...args);
    }
}

window.$consoleError = (...args: any[]) => {
    console.error(...args);
}

window.$ajaxGet = async (url: string | null) => {
    var urlToUse = url ? url : (window.$baseUrl + window.$prefixUrl);
    const res = await fetch(urlToUse, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include'
    }).then((data) => data.json())
        .catch((error) => ({
            errors: [{
                message: error.message,
            }]
        }))

    return res;
}

window.$ajaxPost = async (url: string | null, body: any, options?: {
    headers?: { [key: string]: string },
    onUploadProgress?: (progress: { loaded: number; total: number; }) => void
}) => {
    var urlToUse = url ? url : (window.$baseUrl + window.$prefixUrl);
    const res = await fetch(urlToUse, {
        method: 'POST',
        headers: {
            // 'Content-Type': 'application/json',
            ...options?.headers
        },
        credentials: 'include',
        body: JSON.stringify(body)
    }).then((data) => data.json())
        .catch((error) => ({
            errors: [{
                message: error.message,
            }]
        }))

    return res;
}

window.$ajaxUpload = async (url: string | null, body: any, options?: {
    headers?: { [key: string]: string },
    onUploadProgress?: (progress: { loaded: number; total: number; }) => void
}) => {
    var urlToUse = "";

    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        urlToUse = url;
    } else {
        var prefix = (window.$prefixUrl && window.$prefixUrl.startsWith('/dev'))
            ? window.$prefixUrl.substring(4)
            : window.$prefixUrl;

        if (!prefix.endsWith('/')) prefix += '/';
        if (url && url.startsWith('/')) url = url.substring(1);

        url = url ? url : 'graphql';
        if(url.startsWith('/auth')) { 
            prefix = ''; 
        } else if(url.startsWith('auth')) {
            prefix = '/';
        }

        urlToUse = prefix + (url ? url : 'upload');
    }

    return new Promise((resolve, reject) => {
        if (!url) {
            return reject(new Error("URL is required"));
        }

        const xhr = new XMLHttpRequest();

        // 1. Setup Progress Tracking
        if (options?.onUploadProgress) {
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    options.onUploadProgress!({
                        loaded: event.loaded,
                        total: event.total
                    });
                }
            };
        }

        xhr.open("POST", urlToUse);

        // 2. Set Headers
        if (options?.headers) {
            Object.entries(options.headers).forEach(([key, value]) => {
                xhr.setRequestHeader(key, value);
            });
        }

        // 3. Handle Response
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response);
                } catch (e) {
                    resolve(xhr.responseText);
                }
            } else {
                reject({
                    status: xhr.status,
                    statusText: xhr.statusText,
                    body: xhr.responseText
                });
            }
        };

        xhr.onerror = () => reject(new Error("Network Error"));

        // 4. Send Body (Automatic handling for FormData or JSON)
        if (body instanceof FormData) {
            xhr.send(body);
        } else {
            xhr.setRequestHeader("Content-Type", "application/json");
            xhr.send(JSON.stringify(body));
        }
    });
}

window.$ajaxGraphql = async (query: string, variables?: Variables, url?: string | null,) => {
    var urlToUse = "";

    if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        urlToUse = url;
    } else {
        var prefix = (window.$prefixUrl && window.$prefixUrl.startsWith('/dev'))
            ? window.$prefixUrl.substring(4)
            : window.$prefixUrl;

        if (!prefix.endsWith('/')) prefix += '/';
        if (url && url.startsWith('/')) url = url.substring(1);

        url = url ? url : 'graphql';
        if(url.startsWith('/auth')) { 
            prefix = ''; 
        } else if(url.startsWith('auth')) {
            prefix = '/';
        }

        urlToUse = prefix + url;
    }

    var res = await processGraphql(urlToUse, query, variables ? variables : {});

    if (res && res.data && !res.errors) {
        return res.data;
    } else if (res && (!res.errors || (res.errors.length && res.errors[0].message))) {
        var status = await window.$refreshAccessToken();

        if (status) {
            res = await processGraphql(urlToUse, query, variables ? variables : {});
            if (res && res.data && !res.errors) {
                return res.data;
            }
        } else {
            // var dev = (window.$prefixUrl && window.$prefixUrl.startsWith('/dev'))? '/dev': '';
            // return window.location.replace(`${dev}/auth`);

            return {
                statusCode: 401,
                status: 401,
                errors: ["Not authorized"],
            }
        }

        return { errors: res.errors }
    }

    return res
}

const processGraphql = async (url: string, query: string, variables: Variables) => {
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            "Accept": "application/json",
            "Accept-Content": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({
            query: query,
            variables: variables
        })
    })
        .then((data) => data.json())
        .catch((error) => ({
            errors: [{
                message: error.message,
            }]
        }))

    window.$consoleLog(res)

    if (res && res.error) {
        return { errors: [{ message: res.error }] }
    }

    return res;
}

window.$refreshAccessToken = async (moduleName?: string) => {
    var data = await fetch(`/auth/refresh${moduleName ? `/${moduleName}` : ``}`, {
        method: 'GET',
        credentials: 'include',  // Ensure cookies are sent with the request
        headers: {
            "Accept": "application/json",
            "Accept-Content": "application/json",
        },
    })
        .then(response => response.json())
        .catch(err => console.error('Error:', err));

    if (
        data
        && (
            (data.access_token && data.refresh_token)
            || (data.accessToken && data.refreshToken)
        )
    ) {
        return true;
    }

    return false;
}

window.$getMainDomain = (value: string): string | null => {
    try {
        var domain = null;
        var reg = new RegExp("((?<slug>.*)[.])?(?<domain>[a-zA-Z0-9-_]{3,}[.](?<primary>[a-z]{2,3}([.][a-z]{2,})?))");
        var exe = reg.exec(value);

        if (exe && exe.groups && exe.groups.domain) {
            domain = exe.groups.domain;
        }

        return domain
    } catch (e) {
        console.error(e);
    }

    return null
}

window.$isPermitted = (permission?: string | null, defaultValue: boolean  = false): boolean => {
    const authStore = useAuthStore();
    if(!permission) return true;

    if(authStore.permissions.includes(permission)) {
        return true;
    }

    return defaultValue;
}


window.getHexID = function (length = 24) {
    let result = '';
    let n = '0123456789',
        h = 'ABCDEFabcdef';
    let chars = n + h;

    for (let i = length; i > 0; --i) {
        result += chars[Math.floor(Math.random() * chars.length)];
    }

    return result;
}


window.isEmpty = function (value: any) {
    if (
        value === null ||
        typeof value === 'undefined' ||
        (typeof value === 'string' && value.trim() == '')
    ) {
        return true;
    }

    return false;
}

window.mergeJSON = function (target, source) {
    function isObject(obj: { hasOwnProperty: (arg: string) => any; }) {
        if (typeof obj == "object") {
            for (var key in obj) {
                if (obj.hasOwnProperty(key)) {
                    return true; // search for first object prop
                }
            }
        }
        return false;
    }

    for (var key in source) {
        if (source.hasOwnProperty(key)) {
            if (target[key] && isObject(target[key]) && isObject(source[key])) {
                window.mergeJSON(target[key], source[key]);
            } else {
                target[key] = source[key];
            }
        }
    }
    return target;
}

window.toSlug = function (text) {
    if (!text) return '';

    return text
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space before capital letters (camelCase support)
        .toLowerCase()                       // Convert to lowercase
        .trim()                              // Remove leading/trailing spaces
        .replace(/[^a-z0-9\s-]/g, '')        // Remove all non-word chars except spaces and hyphens
        .replace(/\s+/g, '-')                // Replace spaces with hyphens
        .replace(/-+/g, '-');                // Replace multiple hyphens with single
}

window.camelize = function (str) {
    str = window.toSlug(str)              // Replace multiple hyphens with single
        .replace(/-/g, ' ');

    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match: string, index: number) {
        if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
}

window.toTitle = function (text) {
    if (!text) return '';

    var t = window.toSlug(text)              // Replace multiple hyphens with single
        .replace(/-/g, ' ');

    return t.substring(0, 1).toUpperCase() + t.substring(1)
}

window.toggleClass = (name, options) => {
    var { elem, remove } = options || {}

    if (!elem) elem = document.querySelector('body');
    var hasClass = elem.classList.contains(name);

    if (!hasClass && !remove) {
        elem.classList.add(name);
    } else if (hasClass && remove) {
        elem.classList.remove(name);
    }
}

window.fileToBase64 = async function (file) {
    if (file) {
        const reader = new FileReader();

        return await (new Promise(function (resolve, reject) {
            reader.onload = function (e) {
                resolve({
                    base64: (e.target) ? e.target.result : null,
                    // fileName: file.name,
                    fileType: file.type,
                    fileSize: file.size,
                });
            }
            reader.readAsDataURL(file); // convert to base64 string
        }));
    }

    return null;
}

window.runFunction = (name) => {
    // if(window.appControls && typeof window.appControls[name] == 'function') {
    //     return window.appControls[name];
    // }

    return null;
}

window.isFunction = (name) => {
    // if(window.appControls && typeof window.appControls[name] == 'function') {
    //     return true;
    // }

    console.error(`${name} is not Function`);

    return false;
}

window.padString = function (n: any, width = 6, pad = '0') {
    while (`${n}`.length < width) n = `${pad}` + `${n}`;
    return n;
}

window.padZeroes = function (n: any, width = 6) {
    return window.padString(n, width, '0');
}

window.getColor = function (
    color: string,
    opts?: {
        amount?: number,
        type?: 'rgb' | 'hex',
        empty?: boolean
    }): string {
    opts = opts || {};

    var amount = opts.amount || 0;
    var type = opts.type || 'rgb';
    var empty = opts.empty || false;
    color = (color) ? color.replace(/ /gi, '').trim() : "";

    if (color.startsWith("#")) {
        color = color.slice(1);
    } else if (color.startsWith('rgb(')) {
        var colors = color
            .replace('rgb(', '')
            .replace(')', '')
            .split(',');

        var r = parseInt(colors.pop() || '0');
        var b = parseInt(colors.pop() || '0');
        var g = parseInt(colors.pop() || '0');

        color = (g | (b << 8) | (r << 16)).toString(16);
    }

    var num = parseInt(color, 16);

    var r: number = (num >> 16) + amount;

    if (r > 255) {
        r = 255;
    } else if (r < 0) {
        r = 0;
    }

    var b = ((num >> 8) & 0x00FF) + amount;

    if (b > 255) {
        b = 255;
    } else if (b < 0) {
        b = 0;
    }

    var g = (num & 0x0000FF) + amount;

    if (g > 255) {
        g = 255;
    } else if (g < 0) {
        g = 0;
    }

    if (type == 'rgb') {
        return (empty) ? `${r}, ${b}, ${g}` : `rgb(${r},${b},${g})`;
    };

    return (!empty ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
}

window.getRandomColor = function (index: number): string {
    // config := cb.dataModel.Model.App.Config.Theme;

    var colors: string[] = [
        // Your original 10
        "#9966FF", "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0",
        "#FF9F40", "#FF6384", "#C9CBCF", "#4BC0C0", "#FF6384",

        // 30 New colors in the same tone
        "#55efc4", "#81ecec", "#74b9ff", "#a29bfe", "#dfe6e9",
        "#00b894", "#00cec9", "#0984e3", "#6c5ce7", "#b2bec3",
        "#ffeaa7", "#fab1a0", "#ff7675", "#fd79a8", "#636e72",
        "#fdcb6e", "#e17055", "#d63031", "#e84393", "#2d3436",
        "#badc58", "#dff9fb", "#f9ca24", "#f0932b", "#eb4d4b",
        "#6ab04c", "#c7ecee", "#7ed6df", "#e056fd", "#686de0",
    ]

    return colors[index % colors.length];
}

window.isImage = (url: any): boolean => {
    if (typeof url !== 'string') return false;
    return /\.(jpg|jpeg|png|webp|avif|gif|svg|bmp)(\?.*)?$/i.test(url);
};

window.isNumeric = (value: any): boolean => {
    if (typeof value === 'string') value = value.trim();

    return /^-?\d+(\.\d*)?$/.test(`${value}`);
}
window.isDate = (value: any): boolean => {
    try {
        if (!value) {
            return false;
        } else if (value instanceof Date) {
            return true;
        } else {
            var regExp = /\d{4}-(1[0-2]|0?[1-9])-(3[01]|0?[1-9]|[12][0-9])((\s+|T)((2[0-3]|[01]?[0-9]):([0-5]?[0-9])(:[0-5]?[0-9]((.\d{3}([zZ]))?|([+](2[0-3]|[01]?[0-9])(:[0-5][0-9]))?)?)?)?)?$/;

            return regExp.test(value);
        }
    } catch (error) {
        // console.error(error);
    }

    return false;
}

window.copyToClipboard = async (text: string): Promise<void> => {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    } else {
        let textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";  // Avoid scrolling to bottom
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        return new Promise((res, rej) => {
            document.execCommand('copy') ? res() : rej();
            textArea.remove();
        });
    }
}

window.copy = (value: any) => {
    return JSON.parse(JSON.stringify(value));
}